import { Helmet } from "react-helmet-async";
import { useParams, useNavigate } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { KanbanBoard } from "@/components/kanban/KanbanBoard";
import { boardService, type TaskBoard } from "@/lib/boardService";
import { useWorkspace } from "@/context/WorkspaceContext";
import { toast } from "sonner";
import React from "react";

interface TaskBoardPageProps {
  isGlobalDarkMode?: boolean;
  onMobileSubMenuToggle?: (menuId: string | null, subItems?: any[], menuLabel?: string) => void;
  isMobile?: boolean;
}

const TaskBoardPage = (_props: TaskBoardPageProps) => {
  const { slug = "board" } = useParams();
  const navigate = useNavigate();
  const { currentWorkspace, isClient } = useWorkspace();
  const [board, setBoard] = React.useState<TaskBoard | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const loadBoard = async () => {
      if (!currentWorkspace) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await boardService.getBoardBySlug(currentWorkspace.id, slug);
        if (!data) {
          toast.error('Board not found');
          navigate('/boards');
          return;
        }
        setBoard(data);
      } catch (error) {
        console.error('Error loading board:', error);
        toast.error('Failed to load board');
        navigate('/boards');
      } finally {
        setLoading(false);
      }
    };

    loadBoard();
  }, [currentWorkspace, slug, navigate]);

  const handleHeaderUpdate = async (updates: {
    title?: string;
    description?: string;
    background?: any;
    slug?: string;
    hideBanner?: boolean;
    enableUpvotes?: boolean;
  }) => {
    if (!board || !currentWorkspace) return;

    try {
      // If title changed, generate new slug from database
      let updateData = { ...updates };
      let newSlug: string | undefined;
      if (updates.title && updates.title !== board.title) {
        newSlug = await boardService.generateUniqueSlug(currentWorkspace.id, updates.title);
        updateData = { ...updateData, slug: newSlug };
      }
      
      const updated = await boardService.updateBoard(board.id, updateData);
      setBoard(updated);
      
      // Navigate to new slug if it changed
      if (newSlug && newSlug !== slug) {
        navigate(`/task-board/${newSlug}`, { replace: true });
      }
      
      toast.success('Board updated');
    } catch (error) {
      console.error('Error updating board:', error);
      toast.error('Failed to update board');
      throw error; // Re-throw so HeaderSettingsDialog can handle it
    }
  };

  if (loading || !board) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="w-full px-6 md:px-10 py-12 md:py-16 relative bg-muted/50 flex flex-col items-center justify-center gap-4">
          <div className="absolute top-4 left-6 md:left-10">
            <Skeleton className="h-8 w-20 rounded-md" />
          </div>
          <div className="absolute top-4 right-6 md:right-10">
            <Skeleton className="h-8 w-28 rounded-md" />
          </div>
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-4 w-96 max-w-full" />
          <Skeleton className="h-4 w-72 max-w-full" />
        </div>
        <div className="flex-1 overflow-hidden px-6 md:px-10 py-8 flex flex-col gap-4">
          <div className="flex items-center justify-between flex-shrink-0">
            <Skeleton className="h-7 w-36" />
            <div className="flex gap-3">
              <Skeleton className="h-9 w-28" />
              <Skeleton className="h-9 w-32" />
              <Skeleton className="h-9 w-36" />
            </div>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-4">
            {[1, 2, 3, 4].map((col) => (
              <div key={col} className="min-w-[320px] w-[350px] rounded-xl bg-board-column border border-border/40 p-3 flex flex-col gap-3">
                <div className="flex items-center justify-between px-1">
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-3 w-4 rounded-full" />
                  </div>
                  <Skeleton className="h-6 w-6 rounded" />
                </div>
                <div className="flex flex-col gap-2">
                  {col === 1 ? (
                    <>
                      <div className="rounded-lg bg-card border border-border/60 p-3 flex flex-col gap-2 shadow-sm">
                        <div className="flex items-start justify-between gap-2">
                          <Skeleton className="h-4 w-3/4" />
                          <Skeleton className="h-5 w-14 rounded-full" />
                        </div>
                        <Skeleton className="h-3 w-1/2" />
                        <div className="flex items-center justify-between mt-1">
                          <Skeleton className="h-3 w-20" />
                          <Skeleton className="h-6 w-6 rounded-full" />
                        </div>
                      </div>
                      <div className="rounded-lg bg-card border border-border/60 p-3 flex flex-col gap-2 shadow-sm">
                        <div className="flex items-start justify-between gap-2">
                          <Skeleton className="h-4 w-2/3" />
                          <Skeleton className="h-5 w-14 rounded-full" />
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <Skeleton className="h-3 w-16" />
                          <Skeleton className="h-6 w-6 rounded-full" />
                        </div>
                      </div>
                    </>
                  ) : col === 3 ? (
                    <div className="rounded-lg bg-card border border-border/60 p-3 flex flex-col gap-2 shadow-sm">
                      <div className="flex items-start justify-between gap-2">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-5 w-14 rounded-full" />
                      </div>
                      <Skeleton className="h-3 w-1/2" />
                      <div className="flex items-center justify-between mt-1">
                        <Skeleton className="h-3 w-20" />
                        <Skeleton className="h-6 w-6 rounded-full" />
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const headerCfg = {
    title: board.title,
    description: board.description,
    background: board.background,
    hideBanner: board.hide_banner,
    enableUpvotes: board.enable_upvotes ?? (board.background as any)?.enableUpvotes ?? false,
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{`${headerCfg.title} | Task Board`}</title>
        <meta name="description" content={headerCfg.description} />
        <link rel="canonical" href={`/task-board/${slug}`} />
      </Helmet>

      <main className="w-full min-w-0 px-6 md:px-10 py-8">
        <KanbanBoard
          board={board}
          onBack={() => {
            if (isClient) {
              navigate(-1);
            } else {
              navigate("/task-board");
            }
          }}
          headerConfig={headerCfg}
          onHeaderUpdate={handleHeaderUpdate}
          currentSlug={slug}
        />
      </main>
    </div>
  );
};

export default TaskBoardPage;
